import { useEffect, useState } from 'react'
import { useFormMutation } from '@/common/hooks/useFormMutation'
import { createProduct, type CreateProductRequest, type CreateProductResponse } from '../api'
import { useCurrentOrg } from '@/app/entities/org/model'

export type CreateProductFormData = {
    title: string
    description: string
    marketplace: CreateProductRequest['marketplace'] | ''
    brand: string
    key_image: string
    additional_images: string
}

export const useCreateProductForm = () => {
    const { data: currentOrg } = useCurrentOrg()
    const [keyImageFile, setKeyImageFile] = useState<File | null>(null)
    const [additionalImagePrimary, setAdditionalImagePrimary] = useState<File | null>(null)
    const [additionalImageSecondary, setAdditionalImageSecondary] = useState<File | null>(null)

    const { form, handleSubmit, onSubmit, register, isLoading, isError, isSuccess, error, errors, queryClient, data } =
        useFormMutation<CreateProductFormData, CreateProductResponse, { detail?: string }>({
            formOptions: {
                mode: 'onChange',
                defaultValues: {
                    title: '',
                    description: '',
                    marketplace: '',
                    brand: '',
                    key_image: '',
                    additional_images: '',
                },
            },
            mutationFn: async formData => {
                if (!currentOrg?.id) {
                    return Promise.reject(new Error('Организация не выбрана'))
                }

                if (!keyImageFile) {
                    return Promise.reject(new Error('Ключевое изображение обязательно'))
                }

                const imageFiles: File[] = [keyImageFile]

                if (additionalImagePrimary) {
                    imageFiles.push(additionalImagePrimary)
                }
                if (additionalImageSecondary) {
                    imageFiles.push(additionalImageSecondary)
                }

                const created = await createProduct(currentOrg.id, {
                    title: formData.title,
                    description: formData.description,
                    marketplace: formData.marketplace as CreateProductRequest['marketplace'],
                    ...(formData.brand ? { brand: formData.brand } : {}),
                    image_files: imageFiles,
                })

                return created
            },
            toastOptions: {
                successMessage: 'Товар создан',
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['products-infinite'] })
                setKeyImageFile(null)
                setAdditionalImagePrimary(null)
                setAdditionalImageSecondary(null)
            },
        })

    useEffect(() => {
        form.reset({
            title: '',
            description: '',
            marketplace: '',
            brand: '',
            key_image: '',
            additional_images: '',
        })
    }, [form])

    return {
        form,
        handleSubmit,
        onSubmit,
        register,
        isPending: isLoading,
        isError,
        isSuccess,
        error,
        errors,
        data,
        currentOrgId: currentOrg?.id,
        keyImageFile,
        additionalImagePrimary,
        additionalImageSecondary,
        setKeyImageFile,
        setAdditionalImagePrimary,
        setAdditionalImageSecondary,
    }
}
